import { commonGenders } from "@greenflagdate/shared";
import { Box, chakra, Flex, Text } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { ChoiceCard } from "../ChoiceCard/ChoiceCard";
import { FormInput } from "../FormInput/FormInput";

const ChakraButton = chakra("button");

interface GenderSelectorProps {
  value?: string;
  onChange: (value?: string) => void;
}

export const GenderSelector = ({ value, onChange }: GenderSelectorProps) => {
  const [showMore, setShowMore] = useState(false);
  const [search, setSearch] = useState("");

  const lower = value?.toLowerCase();
  const isPrimary = lower === "man" || lower === "woman";
  const customLabel = !isPrimary && value ? value : undefined;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return commonGenders.filter(
      (g) => g.toLowerCase().includes(q) && g.toLowerCase() !== q
    );
  }, [search]);

  const pick = (g: string) => {
    onChange(g);
    setShowMore(false);
    setSearch("");
  };

  return (
    <Flex direction="column" gap="10px" w="100%">
      <ChoiceCard selected={lower === "woman"} onClick={() => pick("woman")}>
        Woman
      </ChoiceCard>
      <ChoiceCard selected={lower === "man"} onClick={() => pick("man")}>
        Man
      </ChoiceCard>
      {customLabel && (
        <ChoiceCard selected onClick={() => onChange("")}>
          {customLabel}
        </ChoiceCard>
      )}
      {showMore ? (
        <Box>
          <FormInput
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            placeholder="Search or type your gender…"
            autoFocus
          />
          <Box mt="8px" maxH="200px" overflowY="auto">
            {search.trim() && (
              <ChakraButton
                type="button"
                w="100%"
                textAlign="left"
                px="14px"
                py="10px"
                borderRadius="8px"
                bg="transparent"
                cursor="pointer"
                fontFamily="body"
                fontSize="14px"
                color="fg.default"
                _hover={{ bg: "bg.subtle" }}
                onClick={() => pick(search.trim())}
              >
                Use “{search.trim()}”
              </ChakraButton>
            )}
            {filtered.map((g) => (
              <ChakraButton
                key={g}
                type="button"
                w="100%"
                textAlign="left"
                px="14px"
                py="10px"
                borderRadius="8px"
                bg="transparent"
                cursor="pointer"
                fontFamily="body"
                fontSize="14px"
                color="fg.default"
                _hover={{ bg: "bg.subtle" }}
                onClick={() => pick(g)}
              >
                {g}
              </ChakraButton>
            ))}
          </Box>
        </Box>
      ) : (
        <ChakraButton
          type="button"
          alignSelf="flex-start"
          bg="transparent"
          border="0"
          py="10px"
          px="4px"
          cursor="pointer"
          fontFamily="body"
          fontSize="13px"
          fontWeight="medium"
          color="fg.muted"
          onClick={() => setShowMore(true)}
        >
          <Text as="span">More options ▾</Text>
        </ChakraButton>
      )}
    </Flex>
  );
};
